import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  useHostingAdvancedDomainServicesCheck,
  useHostingAdvancedServer,
} from '@newfold/huapi-js';
import {
  HostingSitesV2200ItemsItem,
  UserHosting200RowsItem,
} from '@newfold/huapi-js/src/index.schemas';

import CardTopBanner from '~/components/CardTopBanner';
import Tooltip from '~/components/CommonTooltip';
import CopyWithTooltip from '~/components/CopyWithTooltip';
import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useResponsive from '~/hooks/useResponsive';
import ServerSetupTooltipContent from '~/Nova/scenes/HostingList/components/ServerSetupTooltipContent';
import UsageChart from '~/Nova/scenes/HostingList/components/UsageChart';
import {
  calculatePercentage,
  getDiskTotalGib,
  getDiskUsedGib,
  getHowToCardContent,
  getServerStatusandColor,
  getTotalDiskQuotaGib,
  isSelfManagedDedicated,
} from '~/Nova/scenes/HostingList/utils/helpers';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';
import HowtoCard from '~/Nova/scenes/Site/components/HowtoCard';
import AdvancedHostingResetPasswordModal from '~/scenes/Hosting/scenes/HostingDetail/components/AdvancedHosting/components/ResetPassword/ResetPasswordModal';
import AdvancedHostingServerStatus from '~/scenes/Hosting/scenes/HostingDetail/components/AdvancedHosting/components/Server/ServerStatus';
import AdvancedHostingServerStatusModal from '~/scenes/Hosting/scenes/HostingDetail/components/AdvancedHosting/components/Server/ServerStatusModal';

import InfoCard from '../../../components/InfoCard';
import ChangeHostnameModal from './ChangeHostnameModal';
import OSManageCard from './OSManageCard';

interface OverviewProps {
  hostingPlanDetails?: UserHosting200RowsItem | null;
  installedAppData: HostingSitesV2200ItemsItem | undefined;
  installedAppDataLoading: boolean;
  serverStatus: string;
  setServerStatus: (status: string) => void;
  isReimaging?: boolean;
  reimageFailed?: boolean;
  isOsImagesAvailable: boolean;
  isErrorOSImages: boolean;
  appActionInProgress: boolean;
  osAdvancedImages?: PublicImageItem[];
}

const Overview = ({
  hostingPlanDetails = null,
  installedAppData,
  installedAppDataLoading,
  serverStatus,
  setServerStatus,
  isReimaging = false,
  reimageFailed = false,
  isOsImagesAvailable,
  isErrorOSImages,
  appActionInProgress,
  osAdvancedImages = [],
}: OverviewProps) => {
  const { t } = useTranslation('selfManagedHosting', { keyPrefix: 'overview' });
  const { t: tHosting } = useTranslation('hosting');
  const { id: hostingId } = useAccount();
  const navigate = useNavigate();
  const { isMobile: checkIsMobile } = useResponsive();
  const isMobile = checkIsMobile();
  const [openPasswordResetModal, setOpenPasswordResetModal] = useState(false);
  const [openServerStatusSuccessModal, setOpenServerStatusSuccessModal] =
    useState(false);
  const [showResetPasswordSuccessTxt, setShowResetPasswordSuccessTxt] =
    useState(false);
  const [openChangeHostnameModal, setOpenChangeHostnameModal] = useState(false);

  const { brandFromJWT, vpsOverviewArticleUrl, dediOverviewArticleUrl } =
    useBrandInfo();
  const isHostgator = brandFromJWT?.toLowerCase() === 'hostgator';

  const isServerOnline = serverStatus === 'active';

  const {
    account_info: {
      region = {},
      meta = {},
      software = '',
      image = {},
      type = '',
      server_id = '',
    } = {},
    billing,
    used,
  } = hostingPlanDetails || {};

  const isSelfManagedDedi = isSelfManagedDedicated(type, billing?.prod_name);

  const { data: serverData, isPending: isServerDataPending } =
    useHostingAdvancedServer(hostingId, {
      query: {
        enabled: !!hostingId,
        select: (data) => data?.data,
      },
    });

  const { data: domainServiceData } = useHostingAdvancedDomainServicesCheck(
    hostingId!,
    {
      query: {
        enabled: !!hostingId,
      },
    },
  );

  const isDomainServiceConfigured =
    !domainServiceData?.data?.configure_domain_services;

  const {
    os_name: osImageName,
    os_version: osVersion,
    hostname: hostName,
    ips,
  } = serverData || {};

  const ip = ips?.primary_ip;

  const domainName = meta?.main_domain;
  const dataCenter = region?.desc;
  const installedApp = (image?.app || image?.software) ?? '';

  const totalDiskQuotaGib = getTotalDiskQuotaGib(hostingPlanDetails ?? {});
  const diskUsedInKib = used?.disk_usage ?? 0;

  const diskUsed = getDiskUsedGib(diskUsedInKib, totalDiskQuotaGib);
  const diskTotal =
    typeof totalDiskQuotaGib === 'number' && Number.isFinite(totalDiskQuotaGib)
      ? getDiskTotalGib(totalDiskQuotaGib)
      : totalDiskQuotaGib;

  const { color, status: serverStatusText } =
    getServerStatusandColor(serverStatus);

  const diskUsedPercentage =
    typeof diskUsedInKib === 'number' && Number.isFinite(totalDiskQuotaGib)
      ? calculatePercentage(
          diskUsedInKib,
          Number(totalDiskQuotaGib) * 1024 * 1024, // convert Gib to Kib
        )
      : null;

  const smValue = useMemo(() => {
    const visibleFields = [
      Boolean(dataCenter),
      Boolean(ip || isServerDataPending),
      Boolean(hostName || (!hostName && isDomainServiceConfigured)),
      true, // root password always visible
    ].filter(Boolean).length;

    const itemsPerRow = [3, 2, 1].find((n) => visibleFields % n === 0) ?? 1;
    return (12 / itemsPerRow) as 4 | 6 | 12;
  }, [
    dataCenter,
    ip,
    isServerDataPending,
    hostName,
    isDomainServiceConfigured,
  ]);

  // Modal handlers
  const handleOpenModal = () => setOpenPasswordResetModal(true);
  const handleResetPasswordSuccess = () => {
    setShowResetPasswordSuccessTxt(true);
    setOpenServerStatusSuccessModal(true);
  };
  const handleSuccessfulModalClose = () => {
    setOpenServerStatusSuccessModal(false);
    setShowResetPasswordSuccessTxt(false);
  };
  const handleNavigateToServerSetup = () => {
    navigate('/setup-server', {
      state: {
        hostingId,
        accountType: type,
        serverId: server_id,
      },
    });
  };

  const { title, description, articleUrl } = getHowToCardContent({
    isHostgator,
    isSelfManagedDedi,
    dediOverviewArticleUrl,
    vpsOverviewArticleUrl,
  });

  const tooltipContent = (
    <ServerSetupTooltipContent
      accountType={type}
      hasHostname={Boolean(hostName?.trim())}
    />
  );

  const getReImagingLoader = (() => {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {isReimaging && !appActionInProgress && (
          <CircularProgress size={16} color="inherit" />
        )}
        <Typography variant="body2" color={color}>
          {tHosting(`hostingNova.serverStatusText.${serverStatusText}`)}
        </Typography>
      </Stack>
    );
  })();
  return (
    <Stack mt={4}>
      <Grid container spacing={{ xs: 3, sm: 4 }} marginTop={1}>
        <Grid item xs={12}>
          <Stack>
            {reimageFailed && (
              <CardTopBanner
                title={t('card.appReimageFailed')}
                severity="error"
              />
            )}
            <OSManageCard
              osName={osImageName}
              osVersion={osVersion}
              installedAppData={installedAppData}
              installedAppDataLoading={installedAppDataLoading}
              isServerOnline={isServerOnline}
              domainName={domainName || ''}
              isReimaging={isReimaging}
              appActionInProgress={appActionInProgress}
              isOsImagesAvailable={isOsImagesAvailable}
              isErrorOSImages={isErrorOSImages}
              serverStatus={serverStatus}
              isServerDataPending={isServerDataPending}
              installedSoftware={software ?? ''}
              installedApp={installedApp ?? ''}
              hostingId={hostingId}
              ip={ip}
              osAdvancedImages={osAdvancedImages}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <InfoCard
            title={t('card.title.diskStorage')}
            primaryItem={
              <Typography variant="body2">
                {totalDiskQuotaGib ? (
                  <Trans
                    t={t}
                    i18nKey="card.usedOf"
                    values={{
                      used: diskUsed,
                      total:
                        diskTotal === 'unlimited'
                          ? t('unlimited.unlimited')
                          : diskTotal,
                    }}
                    components={{ b: <b /> }}
                  />
                ) : (
                  t('card.diskUsageNotAvailable')
                )}
              </Typography>
            }
            secondaryItem={{
              itemNode: diskUsedPercentage && (
                <UsageChart
                  percentage={diskUsedPercentage}
                  threshold={80}
                  used={diskUsed}
                  total={diskTotal}
                />
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InfoCard
            title={t('card.title.serverStatus')}
            primaryItem={getReImagingLoader}
            tooltipText={t('card.tooltip.serverStatus')}
            minHeight={totalDiskQuotaGib ? 80 : undefined}
          />
        </Grid>
      </Grid>
      <Stack py={{ xs: 3, sm: 4 }}>
        <AdvancedHostingServerStatus
          handleServerStatusChange={setServerStatus}
          planHostingId={hostingId}
          buttonGap={isMobile ? 3 : 4}
          serverType={type ?? undefined}
        />
      </Stack>
      <Grid container spacing={{ xs: 3, sm: 4 }}>
        {dataCenter && (
          <Grid item xs={12} sm={smValue}>
            <InfoCard
              title={t('card.title.dataCenter')}
              primaryItem={
                <Typography variant="body2">{dataCenter}</Typography>
              }
              tooltipText={t('card.tooltip.dataCenter')}
            />
          </Grid>
        )}
        {(ip || isServerDataPending) && (
          <Grid item xs={12} sm={smValue}>
            <InfoCard
              title={t('card.title.ipAddress')}
              primaryItem={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{ip}</Typography>
                  {ip && <CopyWithTooltip text={ip} />}
                </Stack>
              }
              tooltipText={t('card.tooltip.ip')}
              isLoading={isServerDataPending}
            />
          </Grid>
        )}
        {(hostName || isServerDataPending) && (
          <Grid item xs={12} sm={smValue}>
            <InfoCard
              title={t('card.title.hostname')}
              primaryItem={
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: 'break-all',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {hostName}
                  </Typography>
                  {hostName && <CopyWithTooltip text={hostName} />}
                  {hostName && (
                    <Tooltip title={t('card.changeHostname.iconTooltip')} arrow>
                      <IconButton
                        size="small"
                        onClick={() => setOpenChangeHostnameModal(true)}
                        data-testid="edit-hostname-btn"
                        disabled={!isServerOnline}
                      >
                        <EditIcon
                          fontSize="small"
                          color={isServerOnline ? 'primary' : 'disabled'}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              }
              tooltipText={t('card.tooltip.hostname')}
              isLoading={isServerDataPending}
            />
          </Grid>
        )}
        {!hostName && isDomainServiceConfigured && (
          <Grid item xs={12} sm={smValue}>
            <InfoCard
              title={t('card.title.hostname')}
              primaryItem={
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ pb: 1 }}
                >
                  <WarningIcon color="warning" fontSize="small" />
                  <Link
                    component="button"
                    onClick={handleNavigateToServerSetup}
                    sx={{ textDecoration: 'none' }}
                  >
                    <Typography variant="body2">
                      {t('card.title.runServerSetup')}
                    </Typography>
                  </Link>
                  <Tooltip title={tooltipContent} arrow placement="top">
                    <IconButton size="small" data-testid="info-icon">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
              tooltipText={t('card.tooltip.hostname')}
              isLoading={isServerDataPending}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={smValue}>
          <InfoCard
            title={t('card.title.rootPassword')}
            primaryItem={
              <Stack sx={{ alignItems: 'flex-start' }}>
                <Button
                  variant="text"
                  sx={{ alignSelf: 'flex-start' }}
                  disabled={!isServerOnline}
                  onClick={handleOpenModal}
                >
                  <Typography variant="body2">
                    {t('card.resetPassword')}
                  </Typography>
                </Button>
              </Stack>
            }
            tooltipText={t('card.tooltip.rootPassword')}
          />
        </Grid>
      </Grid>
      {hostName && (
        <ChangeHostnameModal
          open={openChangeHostnameModal}
          onClose={() => setOpenChangeHostnameModal(false)}
          hostingId={hostingId}
          currentHostname={hostName}
          serverType={type}
          isSelfManagedDedi={isSelfManagedDedi}
        />
      )}
      <AdvancedHostingResetPasswordModal
        openPasswordResetModal={openPasswordResetModal}
        setOpenPasswordResetModal={setOpenPasswordResetModal}
        showResetPasswordSuccessModal={handleResetPasswordSuccess}
        hostingPlanId={hostingId}
      />
      <AdvancedHostingServerStatusModal
        openServerStatusSuccessModal={openServerStatusSuccessModal}
        handleSuccessfullModalClose={handleSuccessfulModalClose}
        showResetPasswordSuccessTxt={showResetPasswordSuccessTxt}
      />
      <HowtoCard
        title={title}
        description={description}
        articleUrl={articleUrl}
        displayVideoArticleCard={false}
      />
    </Stack>
  );
};

export default Overview;
